import { useDispatch, useSelector } from '@redux';
import { refreshOrder } from '@redux/orders/actions';
import { Form, Input, InputNumber, Modal, notification } from 'antd';
import { RestInputContext } from 'components/form/RestInputContext';
import { ORDER_TYPES } from 'constants/constants';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DynamicObjectType } from 'types/commons';
import SelectCustom from './VNCitySelector/SelectCustom';
import FormSelect from 'components/form/FormSelect';
import { getDeliveryTypes } from '@redux/delivery/actions';
import FormItem from 'components/form/FormItem';

interface IRefreshOrderModalProps {
  visible: boolean;
  onCancel: () => void;
}

const RefreshOrderModal = ({ visible, onCancel }: IRefreshOrderModalProps) => {
  const dispatch = useDispatch();

  const { t } = useTranslation();
  const [form] = Form.useForm();
  const currentOrder = useSelector<DynamicObjectType>(
    (state) => state.orders.currentData,
  );

  const [loading, setLoading] = useState(false);

  const [deliveryTypes, setDeliveryTypes] = useState([]);

  useEffect(() => {
    dispatch(getDeliveryTypes({ data: {} })).then(
      ({ payload: { data: data } }: DynamicObjectType) => {
        setDeliveryTypes(data?.data);
      },
    );
  }, []);

  const handleSubmit = (values) => {
    setLoading(true);
    dispatch(
      refreshOrder({
        data: {
          id: currentOrder?.id,
          data: {
            ...values,
            shippingFee: Number(values?.shippingFee),
          },
        },
      }),
    ).then((res: DynamicObjectType) => {
      if (!res?.error?.message) {
        setLoading(false);
        notification.success({
          message: 'Success',
          description: 'Refresh order success',
          onClose: () => {
            window.location.reload();
          },
        });
        onCancel();
        window.location.reload();
      }
      setLoading(false);
    });
  };

  return (
    <Modal
      open={visible}
      onCancel={() => {
        form.resetFields();
        onCancel();
      }}
      onOk={() => {
        form.submit();
      }}
      okButtonProps={{
        loading,
      }}
      title={t('orderDetail.action.refreshOrder')}
    >
      <RestInputContext.Provider value={{ form }}>
        <Form
          layout="vertical"
          form={form}
          onFinish={handleSubmit}
          initialValues={currentOrder}
        >
          <SelectCustom
            source="deliveryType"
            resourceData={deliveryTypes}
            valueProp="code"
            titleProp="name"
            header={t('orders.deliveryType')}
            required
          />
          <Form.Item shouldUpdate={(p, c) => p.deliveryType !== c.deliveryType}>
            {({ getFieldValue }) => {
              const deliveryType = getFieldValue('deliveryType');
              return (
                <>
                  {deliveryType !== 'EXTERNAL' && (
                    <SelectCustom
                      source="shippingType"
                      resourceData={ORDER_TYPES.filter(
                        (item) => !['all'].includes(item?.value),
                      )}
                      valueProp="value"
                      titleProp="text"
                      header={t('Delivery Method')}
                      required
                    />
                  )}

                  {deliveryType !== 'EXTERNAL' && (
                    <FormSelect
                      reference={{
                        labelProp: 'name',
                        model: `shipping-carrier`,
                        searchKey: 'name',
                        allowClear: true,
                        initFilters: {
                          filters: [
                            {
                              condition: 'and',
                              key: 'isActive',
                              operator: 'eq',
                              value: true,
                            },
                          ],
                        },
                      }}
                      name="shippingCarrierId"
                      label={t('Delivery partner')}
                      required
                    />
                  )}
                  {deliveryType === 'EXTERNAL' && (
                    <FormItem
                      name="deliveryNote"
                      label="Delivery note"
                      required
                    >
                      <Input.TextArea />
                    </FormItem>
                  )}
                  <FormItem name="shippingFee" label="Delivery fee" required>
                    <InputNumber
                      disabled
                      formatter={(value) => {
                        if (!value) return '';
                        return (
                          `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.') +
                          ' đ'
                        );
                      }}
                      parser={(value) => {
                        if (!value) return '';
                        return value.replace(/ đ/g, '').replace(/\./g, '');
                      }}
                    />
                  </FormItem>
                  <FormSelect
                    reference={{
                      labelProp: 'name',
                      model: `locations`,
                      searchKey: 'name',
                      valueProp: 'id',
                      allowClear: true,
                      labelFormat: (item) => (
                        <div>
                          {item?.name
                            ? `${item?.name} | ${item?.id}`
                            : item?.id}
                        </div>
                      ),
                      initFilters: {
                        filters: [
                          {
                            condition: 'and',
                            key: 'isActive',
                            operator: 'eq',
                            value: true,
                          },
                        ],
                      },
                    }}
                    name="locationId"
                    label={t('orders.warehouse')}
                    required
                  />
                </>
              );
            }}
          </Form.Item>
        </Form>
      </RestInputContext.Provider>
    </Modal>
  );
};

export default RefreshOrderModal;
